// React
import React from 'react'
import PropTypes from 'prop-types'

// Libraries
import { clsx } from 'clsx'

// Components
import BOrderPicker from 'ui/blocks/Picker/types/order'
import BPicker from 'ui/blocks/Picker'
import Icon from 'ui/components/Icon'
import DateTimePicker from 'ui/components/Picker/DateTimePicker'

// Hooks
import { usePicker } from 'ui/components/Picker/hooks'
import { useFormikContext } from 'formik'

// Shared
import moment from 'shared/lib/moment'
import { useTranslation } from 'shared/utils/useTranslation'
import { Field } from 'shared/form'

export const Free = (props) => {
  const picker = usePicker()

  const form = useFormikContext()
  const { t } = useTranslation()

  const from = form.values.starts_at ? new Date(form.values.starts_at) : null
  const to = form.values.stops_at ? new Date(form.values.stops_at) : null

  const isDelivery = props.fulfillmentType === 'delivery'

  const handleChangePeriod = ({ startsAt, stopsAt }) => {
    const date = moment(startsAt || stopsAt)

    startsAt = moment(startsAt || form.values.starts_at)
    stopsAt = moment(stopsAt || form.values.stops_at)

    if (!form.values.starts_at) {
      startsAt = picker.setDefaultTime({ type: 'start', date })
    }

    if (!form.values.stops_at) {
      stopsAt = picker.setDefaultTime({ type: 'stop', date, defaultStartTime: startsAt })
    }

    if (startsAt.isAfter(stopsAt)) {
      // starts_at is after stops_at, move the stops_at with the same diff as before
      stopsAt.add(startsAt.diff(moment(form.values.starts_at)))
    }

    form.setValues({
      ...form.values,
      starts_at: startsAt.toJSON(),
      stops_at: stopsAt.toJSON()
    })
  }

  const handleFieldBlur = (date, type) => {
    if (!picker.isDaySelectable({ type, date })) {
      date = picker.setDefaultTime({ type, date, forceDefaultTime: type === 'start' })

      form.setValues({
        ...form.values,
        [`${type}s_at`]: date
      })
    }
  }

  const handleChangeStartsAt = (startsAt) => {
    handleChangePeriod({ startsAt })
  }

  const handleChangeStopsAt = (stopsAt) => {
    handleChangePeriod({ stopsAt })
  }

  const handleBlurStartsAt = (date) => {
    handleFieldBlur(date, 'start')
  }

  const handleBlurStopsAt = (date) => {
    handleFieldBlur(date, 'stop')
  }

  return (
    <BOrderPicker.Period className={clsx({ 'no-times': !picker.useTimes })} disabled={props.disabled}>
      <BPicker.Field>
        <BPicker.Label>{isDelivery ? t('common.from') : t('common.start.present')}</BPicker.Label>
        <Field
          {...props}
          component={DateTimePicker}
          name="starts_at"
          onChange={handleChangeStartsAt}
          onBlur={handleBlurStartsAt}
          isTimeSelectable={(timestring) => picker.isTimeSelectable({ type: 'start', timestring })}
          blockedDays={(date) => !picker.isDaySelectable({ type: 'start', date })}
          inputDateRef={picker.refs.fromDateRef}
          inputTimeRef={picker.refs.fromTimeRef}
          from={from}
          to={to}
          mode="from"
          data-tid="Starts at picker"
          disabledDate={!!props.disable?.starts_at}
          disabledTime={!!props.disable?.starts_at}
          onMonthChange={props.onMonthChange?.bind(null, { type: 'start' })}
          onOpen={props.onOpen?.bind(null, { type: 'start', values: form.values })}
          withAvailability
          hideLabel
          initialError
        />
      </BPicker.Field>
      <BPicker.Decorator>
        <Icon icon="arrow-right" />
      </BPicker.Decorator>
      <BPicker.Field>
        <BPicker.Label>{isDelivery ? t('common.until') : t('common.stop.present')}</BPicker.Label>
        <Field
          {...props}
          component={DateTimePicker}
          name="stops_at"
          onChange={handleChangeStopsAt}
          onBlur={handleBlurStopsAt}
          isTimeSelectable={(timestring) => picker.isTimeSelectable({ type: 'stop', timestring })}
          blockedDays={(date) => !picker.isDaySelectable({ type: 'stop', date })}
          from={from}
          to={to}
          inputDateRef={picker.refs.tillDateRef}
          inputTimeRef={picker.refs.tillTimeRef}
          mode="to"
          data-tid="Stops at picker"
          disabledDate={!!props.disable?.stops_at}
          disabledTime={!!props.disable?.stops_at}
          onMonthChange={props.onMonthChange?.bind(null, { type: 'stop', values: form.values })}
          onOpen={props.onOpen?.bind(null, { type: 'stop', values: form.values })}
          withAvailability
          hideLabel
          initialError
        />
      </BPicker.Field>
    </BOrderPicker.Period>
  )
}

Free.propTypes = {
  disable: PropTypes.shape({
    starts_at: PropTypes.bool,
    stops_at: PropTypes.bool
  }),
  fulfillmentType: PropTypes.oneOf(['pickup', 'delivery']),
  onMonthChange: PropTypes.func,
  onOpen: PropTypes.func,
  disabled: PropTypes.bool
}
